import { useState } from 'react';
import { Star, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Popup shown when the user stops a CO2-desensitization exercise.
 * The user rates the "air hunger" they felt on a 1-5 star scale.
 * A tailored recommendation is then shown; a button applies it to
 * the parent exercise modal.
 *
 * Props:
 * - isOpen: bool
 * - onClose: () => void
 * - sessionId: string | null  → id of the just-saved session (attaches the rating)
 * - onApplyRecommendation: (direction: 'up' | 'down-strong') => void  → parent modal
 *     applies the difficulty change on its own state.
 */
const AirHungerRatingModal = ({ isOpen, onClose, sessionId, onApplyRecommendation }) => {
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const displayRating = hovered || rating;

  const getRecommendation = (n) => {
    if (n <= 2) {
      return {
        direction: 'up',
        title: 'Augmentez la difficulté',
        body: "La soif d'air ressentie est faible. Passez au cran de difficulté supérieur pour continuer à progresser.",
        color: '#0EA5E9',
        applyLabel: 'Augmenter la difficulté',
      };
    }
    if (n <= 4) {
      return {
        direction: null,
        title: 'Continuez à ce niveau',
        body: "Pratiquez cet exercice quotidiennement jusqu'à ce que la soif d'air ressentie baisse à 1 ou 2 étoiles, puis vous pourrez augmenter la difficulté.",
        color: '#10B981',
        applyLabel: null,
      };
    }
    return {
      direction: 'down-strong',
      title: 'Diminuez considérablement la difficulté',
      body: "La soif d'air est trop intense. Redescendez fortement le niveau de difficulté jusqu'à ne plus ressentir plus de 3 étoiles de soif d'air.",
      color: '#F97316',
      applyLabel: 'Diminuer la difficulté',
    };
  };

  const reco = rating > 0 ? getRecommendation(rating) : null;

  const persistRating = async () => {
    if (!sessionId || rating === 0) return;
    try {
      setSubmitting(true);
      await axios.patch(`${API}/sessions/${sessionId}/rating`, {
        air_hunger_rating: rating,
      });
    } catch (err) {
      console.error('Failed to save rating:', err?.response?.data || err.message);
      toast.error("Impossible d'enregistrer la note.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = async () => {
    await persistRating();
    setRating(0);
    setHovered(0);
    onClose();
  };

  const handleApply = async () => {
    await persistRating();
    if (reco?.direction && onApplyRecommendation) {
      onApplyRecommendation(reco.direction);
      toast.success('Difficulté ajustée pour la prochaine session.');
    }
    setRating(0);
    setHovered(0);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(8px)' }}
      onClick={handleClose}
      data-testid="air-hunger-rating-modal"
    >
      <div
        className="w-full max-w-md rounded-3xl bg-white p-6 sm:p-8 relative shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 transition-colors"
          data-testid="air-hunger-close-btn"
          aria-label="Fermer"
        >
          <X size={20} className="text-slate-500" />
        </button>

        <h3
          className="text-xl sm:text-2xl font-semibold text-slate-900 text-center pr-6"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Évaluer la soif d&apos;air ressentie lors de l&apos;exercice
        </h3>

        <div className="mt-6 flex items-center justify-center gap-2 sm:gap-3">
          {[1, 2, 3, 4, 5].map((n) => {
            const filled = n <= displayRating;
            return (
              <button
                key={n}
                onClick={() => setRating(n)}
                onMouseEnter={() => setHovered(n)}
                onMouseLeave={() => setHovered(0)}
                className="transition-transform hover:scale-110 active:scale-95"
                data-testid={`air-hunger-star-${n}`}
                aria-label={`${n} étoile${n > 1 ? 's' : ''}`}
              >
                <Star
                  size={40}
                  strokeWidth={1.8}
                  className={filled ? 'text-amber-400' : 'text-slate-300'}
                  fill={filled ? '#FACC15' : 'transparent'}
                />
              </button>
            );
          })}
        </div>

        {reco && (
          <div
            className="mt-6 rounded-2xl p-5 border"
            style={{
              backgroundColor: 'rgba(248,250,252,0.9)',
              borderColor: reco.color,
              borderLeftWidth: '4px',
            }}
            data-testid="air-hunger-recommendation"
          >
            <p className="text-base font-semibold mb-2" style={{ color: reco.color }}>
              {reco.title}
            </p>
            <p className="text-sm text-slate-700 leading-relaxed">{reco.body}</p>
          </div>
        )}

        <div className="mt-6 flex flex-col gap-2">
          {reco?.direction && onApplyRecommendation && (
            <button
              onClick={handleApply}
              disabled={submitting}
              className="w-full py-3 rounded-full font-medium text-white transition-all hover:shadow-lg disabled:opacity-60"
              style={{ backgroundColor: reco.color }}
              data-testid="air-hunger-apply-btn"
            >
              {reco.applyLabel}
            </button>
          )}
          <button
            onClick={handleClose}
            disabled={submitting}
            className="w-full py-3 rounded-full font-medium transition-all disabled:opacity-60"
            style={{
              backgroundColor: reco?.direction && onApplyRecommendation ? '#F1F5F9' : '#0EA5E9',
              color: reco?.direction && onApplyRecommendation ? '#334155' : '#FFFFFF',
            }}
            data-testid="air-hunger-done-btn"
          >
            {rating > 0 ? 'Terminer' : 'Passer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AirHungerRatingModal;
